import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight, Menu, X } from "lucide-react";
import { TopBar } from "./TopBar";
import { Logo } from "./Logo";
import { useWizard } from "./WizardProvider";

const LINKS_LEFT = [
  { id: "pillars", label: "The Pillars" },
  { id: "calculator", label: "Yield Calculator" },
];

const LINKS_RIGHT = [
  { id: "have-a-site", label: "Have a Site?" },
  { id: "lifestyle", label: "NSW Lifestyle" },
];

const SERVICES_MENU = [
  { label: "Childcare Assets", to: "/pillars/childcare", color: "#FF1744" },
  { label: "Gated Communities", to: "/pillars/gated-communities", color: "#FFC107" },
  { label: "Bespoke Villas", to: "/pillars/villas", color: "#D500F9" },
  { label: "Commercial Complexes", to: "/pillars/commercial", color: "#00B0FF" },
  { label: "Custom Builds & Knock-Down Rebuilds", to: "/pillars/custom-builds", color: "#00E676" },
];

const ServiceLinks = ({ onNavigate }) => (
  <>
    {SERVICES_MENU.map((s) => (
      <Link
        key={s.to}
        to={s.to}
        onClick={onNavigate}
        data-testid={`nav-service-${s.to.split("/").pop()}`}
        className="group flex items-center gap-3 rounded-xl px-4 py-3 hover:bg-white/[0.06] transition-colors duration-200"
      >
        <span
          className="w-2 h-2 rounded-full shrink-0 transition-transform duration-200 group-hover:scale-150"
          style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }}
        />
        <span className="font-display text-sm font-medium text-white/75 group-hover:text-white transition-colors duration-200 flex-1">
          {s.label}
        </span>
        <ArrowUpRight
          size={15}
          className="text-white/25 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200"
        />
      </Link>
    ))}
  </>
);

export const Navbar = () => {
  const [storyOpen, setStoryOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lenis = useLenis();
  const navigate = useNavigate();
  const location = useLocation();
  const { openWizard } = useWizard();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Over the homepage hero the header floats transparent — links become high-contrast white.
  const transparent = !scrolled && location.pathname === "/" && !mobileMenu;
  const linkTone = transparent
    ? "text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)] hover:text-white"
    : "text-white/60 hover:text-white";

  const scrollTo = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          if (lenis) lenis.scrollTo(el, { offset: -110, duration: 1.4 });
          else el.scrollIntoView({ behavior: "smooth" });
        }
      }, 700);
      return;
    }
    if (lenis) {
      lenis.scrollTo(`#${id}`, { offset: -110, duration: 1.4 });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[95] transition-colors duration-500 ${transparent ? "" : "glass"}`} data-testid="site-header">
        {!transparent && <TopBar />}
        <nav className={`flex items-center justify-between gap-4 h-16 px-6 lg:px-10 border-b transition-colors duration-500 ${transparent ? "border-transparent" : "border-white/10"}`} data-testid="main-navbar">
          {/* Left column — desktop links */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 flex-1 min-w-0">
            {LINKS_LEFT.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className={`font-display text-[13px] font-medium tracking-wide ${linkTone} transition-colors duration-300 whitespace-nowrap`}
              >
                {l.label}
              </button>
            ))}
            <div
              className="relative shrink-0"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                data-testid="nav-services-dropdown"
                onClick={() => setServicesOpen(!servicesOpen)}
                className={`font-display text-[13px] font-medium tracking-wide ${linkTone} transition-colors duration-300 inline-flex items-center gap-1.5`}
              >
                Services
                <ChevronDown size={14} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.98 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 glass rounded-2xl border border-white/15 p-2 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
                    data-testid="nav-services-panel"
                  >
                    <ServiceLinks onNavigate={() => setServicesOpen(false)} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Hamburger — mobile/tablet */}
          <button
            data-testid="nav-hamburger"
            onClick={() => setMobileMenu(!mobileMenu)}
            aria-label="Toggle menu"
            className="lg:hidden shrink-0 w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors duration-300"
          >
            {mobileMenu ? <X size={18} /> : <Menu size={18} />}
          </button>

          {/* Center column — logo hidden while the hero glass-mold logo is active (one logo rule) */}
          {!transparent && (
            <div
              className="shrink-0 flex items-center px-2"
              onMouseEnter={() => setStoryOpen(true)}
              onMouseLeave={() => setStoryOpen(false)}
              data-testid="logo-story-trigger"
            >
              <div className="max-h-12 flex items-center overflow-hidden">
                <Logo width={100} />
              </div>
            </div>
          )}

          {/* Right column — desktop actions */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 flex-1 min-w-0 justify-end">
            <div className="hidden 2xl:flex items-center gap-8">
              {LINKS_RIGHT.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className={`font-display text-[13px] font-medium tracking-wide ${linkTone} transition-colors duration-300 whitespace-nowrap`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <button
              data-testid="nav-contact-cta"
              onClick={() => scrollTo("contact")}
              className="font-display text-[13px] font-semibold tracking-wide rounded-full px-5 py-2 text-white/80 border border-white/20 hover:border-[#FF1744]/60 hover:text-white transition-colors duration-300 whitespace-nowrap"
            >
              Contact Us
            </button>
            <button
              data-testid="project-query-wizard-trigger"
              onClick={() => openWizard()}
              className="font-display text-[13px] font-semibold tracking-wide rounded-full px-5 py-2 text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] shadow-[0_0_20px_rgba(255,23,68,0.35)] hover:shadow-[0_0_32px_rgba(255,23,68,0.6)] hover:scale-105 transition-all duration-300 animate-pulse-glow whitespace-nowrap"
            >
              Project Query Wizard
            </button>
          </div>

          {/* Right column — mobile wizard shortcut */}
          <div className="lg:hidden shrink-0">
            <button
              data-testid="project-query-wizard-trigger-mobile"
              onClick={() => openWizard()}
              className="font-display text-[13px] font-semibold tracking-wide rounded-full px-4 py-2 text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] shadow-[0_0_20px_rgba(255,23,68,0.35)] transition-all duration-300"
            >
              Query
            </button>
          </div>
        </nav>

        {/* Mobile menu — links + services */}
        <AnimatePresence>
          {mobileMenu && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden border-b border-white/10 px-6 py-4"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col">
                {[...LINKS_LEFT, ...LINKS_RIGHT, { id: "contact", label: "Contact Us" }].map((l) => (
                  <button
                    key={l.id}
                    data-testid={`mobile-nav-${l.id}`}
                    onClick={() => {
                      setMobileMenu(false);
                      scrollTo(l.id);
                    }}
                    className="text-left font-display text-sm font-medium text-white/70 hover:text-white py-3 border-b border-white/5 transition-colors duration-200"
                  >
                    {l.label}
                  </button>
                ))}
                <p className="font-display text-[10px] font-bold tracking-[0.3em] text-white/40 mt-4 mb-1">
                  SERVICES
                </p>
                <ServiceLinks onNavigate={() => setMobileMenu(false)} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AnimatePresence>
        {storyOpen && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-none px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setStoryOpen(true)}
              onMouseLeave={() => setStoryOpen(false)}
              className="pointer-events-auto glass rounded-3xl border border-white/15 p-10 max-w-xl w-full shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
              data-testid="brand-story-modal"
            >
              <p className="font-display text-[11px] font-bold tracking-[0.3em] text-[#FF1744] mb-3">
                THE JAIHOMES STORY
              </p>
              <h3 className="font-serif-lux text-4xl text-white leading-tight mb-5">
                Built on a handshake.{" "}
                <em className="text-gradient-fire">Raised on vision.</em>
              </h3>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                JAI HOMES began with a simple promise made under a tin roof in Western Sydney:
                build places where families, investors and communities don't just live — they
                thrive. Today we're a master builder and developer shaping NSW's growth corridors,
                from high-yield childcare assets to gated masterplanned communities.
              </p>
              <p className="text-sm text-white/70 leading-relaxed">
                Every site we touch carries the same signature: architectural precision, genuine
                Aussie warmth, and a relentless commitment to the generations who'll call it home.
              </p>
              <div className="mt-6 flex gap-6">
                {[
                  ["5", "Property Pillars"],
                  ["NSW", "Wide Delivery"],
                  ["iCIRT", "Compliant"],
                ].map(([num, label]) => (
                  <div key={label}>
                    <p className="font-display text-xl font-bold text-white">{num}</p>
                    <p className="text-[11px] text-white/50 tracking-wide">{label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
